import { useEffect, useState } from "react";
import { Sparkles, Rocket, TrendingUp, Calendar, MoreHorizontal, XCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { formatDateLong } from "@/lib/format";

export default function SponsorsAdmin() {
  const [data, setData] = useState({ events: [], total_revenue: 0, total_orders: 0 });
  const [busyId, setBusyId] = useState(null);

  const load = () => api.get("/admin/sponsors").then((r) => setData(r.data));
  useEffect(() => { load(); }, []);

  const extend = async (ev, days) => {
    setBusyId(ev.id);
    try {
      await api.post(`/admin/sponsors/${ev.id}/extend`, { days });
      toast.success(`Extended by ${days} days`);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not extend");
    } finally { setBusyId(null); }
  };

  const endBoost = async (ev) => {
    if (!window.confirm(`End sponsorship for "${ev.title}"?`)) return;
    setBusyId(ev.id);
    try {
      await api.post(`/admin/sponsors/${ev.id}/end`);
      toast.success("Sponsorship ended");
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    } finally { setBusyId(null); }
  };

  const daysLeft = (iso) => {
    if (!iso) return null;
    const ms = new Date(iso).getTime() - Date.now();
    if (ms <= 0) return 0;
    return Math.ceil(ms / (1000 * 60 * 60 * 24));
  };

  const TIER_LABEL = { boost_7d: "7-Day Boost", boost_30d: "30-Day Spotlight" };

  return (
    <div>
      <div className="flex items-end justify-between mb-6">
        <div>
          <h1 className="font-display text-4xl font-black">Sponsored Boosts</h1>
          <p className="text-gray-500 mt-1">Track paid promotions, revenue and expiry.</p>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
        <StatCard icon={TrendingUp} label="Total revenue" value={`₹${(data.total_revenue || 0).toLocaleString()}`} accent />
        <StatCard icon={Rocket} label="Paid orders" value={data.total_orders || 0} />
        <StatCard icon={Sparkles} label="Active sponsors" value={data.events.filter((e) => !e.is_expired).length} />
      </div>

      <div className="bg-white rounded-2xl border border-black/10 overflow-hidden">
        <table className="w-full text-sm" data-testid="sponsors-table">
          <thead className="bg-[#F9F8F6] text-xs uppercase tracking-wider text-gray-500 border-b border-black/10">
            <tr>
              <th className="text-left px-6 py-3 font-bold">Event</th>
              <th className="text-left px-6 py-3 font-bold">Tier</th>
              <th className="text-left px-6 py-3 font-bold">Expires</th>
              <th className="text-left px-6 py-3 font-bold">Revenue</th>
              <th className="text-left px-6 py-3 font-bold">Orders</th>
              <th className="text-right px-6 py-3 font-bold">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-black/5">
            {data.events.map((ev) => {
              const dl = daysLeft(ev.sponsored_until);
              return (
                <tr key={ev.id} data-testid={`sponsor-row-${ev.id}`} className="hover:bg-[#F9F8F6]">
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-3">
                      <img src={ev.image_url} alt="" className="h-10 w-14 rounded-md object-cover bg-gray-100" />
                      <div>
                        <div className="font-bold line-clamp-1">{ev.title}</div>
                        <div className="text-xs text-gray-500 capitalize">{ev.city_key}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4"><span className="text-xs font-bold px-2 py-1 rounded-full bg-[#FA003F]/10 text-[#FA003F] whitespace-nowrap">{TIER_LABEL[ev.sponsor_tier] || ev.sponsor_tier || "—"}</span></td>
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-1.5">
                      <Calendar className="h-3.5 w-3.5 text-gray-400" />
                      <span className="text-xs">{ev.sponsored_until ? formatDateLong(ev.sponsored_until.slice(0, 10)) : "—"}</span>
                    </div>
                    <div className={`mt-1 text-[10px] font-black uppercase tracking-widest ${ev.is_expired ? "text-red-600" : dl <= 3 ? "text-amber-600" : "text-emerald-700"}`}>
                      {ev.is_expired ? "Expired" : dl != null ? `${dl} day${dl === 1 ? "" : "s"} left` : ""}
                    </div>
                  </td>
                  <td className="px-6 py-4 font-bold">₹{(ev.revenue || 0).toLocaleString()}</td>
                  <td className="px-6 py-4 text-center">{ev.orders_count}</td>
                  <td className="px-6 py-4 text-right">
                    <div className="inline-flex gap-2">
                      <Button
                        data-testid={`extend-7d-${ev.id}`}
                        disabled={busyId === ev.id}
                        onClick={() => extend(ev, 7)}
                        variant="outline"
                        className="h-9 rounded-full text-xs font-bold px-3"
                      >
                        +7 days
                      </Button>
                      <Button
                        data-testid={`extend-30d-${ev.id}`}
                        disabled={busyId === ev.id}
                        onClick={() => extend(ev, 30)}
                        className="h-9 rounded-full text-xs font-bold px-3 bg-[#FA003F] hover:bg-[#C40031]"
                      >
                        +30 days
                      </Button>
                      <button
                        data-testid={`end-sponsor-${ev.id}`}
                        onClick={() => endBoost(ev)}
                        title="End sponsorship"
                        className="h-9 w-9 rounded-full border border-red-200 text-red-600 grid place-items-center hover:bg-red-50"
                      >
                        <XCircle className="h-4 w-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
            {data.events.length === 0 && (
              <tr><td colSpan={6} className="px-6 py-16 text-center text-gray-500">No sponsored events yet. Boost one from its event page.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function StatCard({ icon: Icon, label, value, accent }) {
  return (
    <div className={`rounded-2xl border p-5 ${accent ? "bg-[#1A1A1A] text-white border-transparent" : "bg-white border-black/10"}`}>
      <div className={`text-xs font-bold uppercase tracking-widest ${accent ? "text-white/60" : "text-gray-500"}`}>{label}</div>
      <div className="mt-2 flex items-baseline justify-between">
        <div className={`font-display text-3xl font-black ${accent ? "text-white" : "text-[#1A1A1A]"}`}>{value}</div>
        <Icon className={`h-6 w-6 ${accent ? "text-[#FA003F]" : "text-[#FA003F]/60"}`} />
      </div>
    </div>
  );
}
